import { create } from 'zustand';

import type { FileDto } from '@/api/api.dto';
import {
  API,
  deleteRequest,
  getRequest,
  patchRequest,
  postRequest,
  putRequest,
} from '@/api/api.ts';
import type {
  AddContactsRequest,
  AddEmergencyContactsRequest,
  Connection,
  DeletePersonRolesRequest,
  GetConnectionsParams,
  Person,
  PersonPreferences,
  PersonPresenceStatus,
  PersonRole,
  SendConnectionRequest,
  Subscription,
  SubscriptionPlan,
  SubscriptionPrice,
  UpdateContactRequest,
  UpdateEmergencyContactRequest,
  UpdatePersonRequest,
  UpdateReinviteRequest,
} from '@/store/persons/person.dto.ts';
import { invalidateAuthorizedFile } from '@/utils/file-links';
import type { UserRole } from '@/utils/roles';

import {
  acceptConnection,
  assignRoleToPerson,
  createSubscriptionCheckout,
  createSubscriptionPortal,
  declineConnection,
  deleteConnection,
  deletePersonRoles,
  getConnectionsByUserId,
  getMyConnections,
  getPendingConnections,
  getPeopleByIds,
  getSubscriptionPrices,
  getSubscriptions,
  sendConnection,
  setPersonPresence,
  updateReinvite,
} from './person.api';

type PersonState = {
  people: Person[];
  hasFetchedPeople: boolean;
  person: Person | null;
  currentUser: Person | null;
  subscriptions?: Subscription[];
  activeSubscription: Subscription | null;
  getPeople: (params?: { role?: UserRole; order_by?: string }) => Promise<void>;
  getPeopleByService: (
    serviceId: string,
    params?: { role?: UserRole; order_by?: string },
  ) => Promise<Person[]>;
  getPerson: (personId: string) => Promise<void>;
  me: () => Promise<void>;
  updatePerson: (personId: string, data: UpdatePersonRequest) => Promise<void>;
  updateAvatar: (personId: string, file: File) => Promise<FileDto>;
  addContacts: (personId: string, data: AddContactsRequest) => Promise<void>;
  updateContacts: (personId: string, data: UpdateContactRequest) => Promise<void>;
  deleteContacts: (personId: string, contactIds: string[]) => Promise<void>;
  loading: boolean;
  error: string | null;
  assignRoleToPerson: (role: PersonRole) => Promise<void>;
  deletePersonRoles: (request: DeletePersonRolesRequest) => Promise<void>;
  setPresence: (personId: string, presence: PersonPresenceStatus) => Promise<void>;
  createSubscriptionCheckout: (plan: SubscriptionPlan) => Promise<string>;
  getSubscriptions: () => Promise<void>;
  getActiveSubscription: () => Promise<void>;
  createSubscriptionPortal: () => Promise<string>;
  getSubscriptionPrices: (lookupKeys: string[]) => Promise<SubscriptionPrice[]>;
  getPeopleByIds: (ids: string[]) => Promise<Person[]>;
  changePreference: (personId: string, data: PersonPreferences) => Promise<void>;
  addEmergencyContacts: (personId: string, data: AddEmergencyContactsRequest) => Promise<void>;
  updateEmergencyContacts: (personId: string, data: UpdateEmergencyContactRequest) => Promise<void>;
  deleteEmergencyContacts: (personId: string, contactIds: string[]) => Promise<void>;
  connections: Connection[];
  pendingConnections: Connection[];
  sendConnection: (data: SendConnectionRequest) => Promise<void>;
  getMyConnections: (params?: GetConnectionsParams) => Promise<Connection[]>;
  getPendingConnections: (params?: GetConnectionsParams) => Promise<void>;
  acceptConnection: (id: string) => Promise<void>;
  declineConnection: (id: string) => Promise<void>;
  updateReinvite: (id: string, data: UpdateReinviteRequest) => Promise<void>;
  deleteConnection: (id: string) => Promise<void>;
  getConnectionsByUserId: (userId: string, params?: GetConnectionsParams) => Promise<Connection[]>;
};

let isFetchingPeople = false;

export const usePerson = create<PersonState>((set) => ({
  person: null,
  people: [],
  hasFetchedPeople: false,
  currentUser: null,
  activeSubscription: null,
  loading: false,
  error: null,
  connections: [],
  pendingConnections: [],
  getPeopleByService: async (serviceId, params) => {
    try {
      const response = await getRequest<any>(
        API.PEOPLE,
        '',
        false,
        {
          limit: 1000,
          page: 1,
          ...(params?.role && { role: params.role }),
          ...(params?.order_by && { order_by: params.order_by }),
        },
        serviceId,
      );

      return Array.isArray(response.data?.items) ? (response.data.items as Person[]) : [];
    } catch (error) {
      console.error('Error fetching people for service:', error);
      return [];
    }
  },
  getPeople: async (params) => {
    if (isFetchingPeople) return;
    isFetchingPeople = true;
    try {
      set({ loading: true });
      const response = await getRequest<any>(API.PEOPLE, '', false, {
        limit: 1000,
        page: 1,
        ...(params?.role && { role: params.role }),
        ...(params?.order_by && { order_by: params.order_by }),
      });

      set({
        people: Array.isArray(response.data?.items) ? response.data.items : [],
        hasFetchedPeople: true,
        loading: false,
        error: null,
      });
    } catch (error: any) {
      console.error('Error fetching people:', error);
      set({
        loading: false,
        hasFetchedPeople: true,
        error: error.message,
      });
    } finally {
      isFetchingPeople = false;
    }
  },
  getPerson: async (personId: string) => {
    try {
      set({ loading: true });
      const response = await getRequest<Person>(API.PEOPLE, `/${personId}`);

      set({
        person: response.data,
        loading: false,
        error: null,
      });
    } catch (error: any) {
      set({
        loading: false,
        error: error.message,
      });
    }
  },
  me: async () => {
    try {
      set({ loading: true });
      const response = await getRequest<Person>(API.PEOPLE, '/me');

      set({
        currentUser: response.data,
        loading: false,
        error: null,
      });
    } catch (error: any) {
      console.error('Error fetching current user:', error);
      set({
        loading: false,
        error: error.message,
      });
    }
  },
  updatePerson: async (personId: string, data: UpdatePersonRequest) => {
    try {
      set({ loading: true });
      const response = await putRequest<Person, UpdatePersonRequest>(
        API.PEOPLE,
        `/${personId}`,
        data,
      );

      set({
        person: response.data,
        loading: false,
        error: null,
      });
    } catch (error: any) {
      console.error('Error updating person:', error);
      set({
        loading: false,
        error: error.message,
      });
    }
  },
  updateAvatar: async (personId: string, file: File) => {
    try {
      set({ loading: true });
      const formData = new FormData();
      formData.append('avatar', file);

      const response = await patchRequest<FileDto, FormData>(
        API.PEOPLE,
        `/${personId}/avatar`,
        formData,
      );

      invalidateAuthorizedFile(response.data.link);

      // Refresh the current user to get the updated avatar URL
      const userResponse = await getRequest<Person>(API.PEOPLE, '/me');
      set({
        currentUser: userResponse.data,
        loading: false,
        error: null,
      });

      return response.data;
    } catch (error: any) {
      console.error('Error updating avatar:', error);
      set({
        loading: false,
        error: error.message,
      });
      throw error;
    }
  },
  addContacts: async (personId: string, data: AddContactsRequest) => {
    try {
      set({ loading: true });
      const response = await postRequest<Person, AddContactsRequest>(
        API.PEOPLE,
        `/${personId}/contacts`,
        data,
      );

      set({
        person: response.data,
        loading: false,
        error: null,
      });
    } catch (error: any) {
      console.error('Error adding contacts:', error);
      set({
        loading: false,
        error: error.message,
      });
      throw error;
    }
  },
  updateContacts: async (personId: string, data: UpdateContactRequest) => {
    try {
      set({ loading: true });
      const response = await putRequest<Person, UpdateContactRequest>(
        API.PEOPLE,
        `/${personId}/contacts`,
        data,
      );

      set({
        person: response.data,
        loading: false,
        error: null,
      });
    } catch (error: any) {
      console.error('Error updating contacts:', error);
      set({
        loading: false,
        error: error.message,
      });
      throw error;
    }
  },
  deleteContacts: async (personId: string, contactIds: string[]) => {
    try {
      set({ loading: true });
      const response = await deleteRequest<Person>(
        API.PEOPLE,
        `/${personId}/contacts`,
        undefined,
        false,
        { ids: contactIds },
      );

      set({
        person: response.data,
        loading: false,
        error: null,
      });
    } catch (error: any) {
      console.error('Error deleting contacts:', error);
      set({
        loading: false,
        error: error.message,
      });
      throw error;
    }
  },
  assignRoleToPerson: async (role: PersonRole) => {
    await assignRoleToPerson(role);
  },
  deletePersonRoles: async (request: DeletePersonRolesRequest) => {
    await deletePersonRoles(request);
  },
  setPresence: async (personId: string, presence: PersonPresenceStatus) => {
    await setPersonPresence(personId, presence);
    await usePerson.getState().getPeople();
  },
  createSubscriptionCheckout: async (plan: SubscriptionPlan) => {
    return await createSubscriptionCheckout(plan);
  },
  getSubscriptions: async () => {
    try {
      const subscriptions = await getSubscriptions();
      set({ subscriptions });
    } catch (error: any) {
      console.error('Error fetching subscriptions:', error);
      set({
        error: error.message,
      });
    }
  },
  getActiveSubscription: async () => {
    try {
      const subscriptions = await getSubscriptions();
      const activeSubscription = subscriptions.find((sub: Subscription) => sub.status);
      set({ activeSubscription: activeSubscription || null });
    } catch (error: any) {
      console.error('Error fetching active subscription:', error);
      set({
        error: error.message,
      });
    }
  },
  createSubscriptionPortal: async () => {
    try {
      return await createSubscriptionPortal();
    } catch (error: any) {
      console.error('Error creating subscription portal:', error);
      throw error;
    }
  },
  getSubscriptionPrices: async (lookupKeys: string[]) => {
    return await getSubscriptionPrices(lookupKeys);
  },
  getPeopleByIds: async (ids: string[]) => {
    try {
      return await getPeopleByIds(ids);
    } catch (error: any) {
      console.error('Error fetching people by IDs:', error);
      throw error;
    }
  },
  changePreference: async (personId: string, data: PersonPreferences) => {
    try {
      set({ loading: true });
      const response = await putRequest<Person, { preferences: PersonPreferences }>(
        API.PEOPLE,
        `/${personId}/preferences`,
        { preferences: data },
      );

      set({
        person: response.data,
        loading: false,
        error: null,
      });
    } catch (error: any) {
      console.error('Error updating preferences:', error);
      set({
        loading: false,
        error: error.message,
      });
      throw error;
    }
  },
  addEmergencyContacts: async (personId: string, data: AddEmergencyContactsRequest) => {
    try {
      set({ loading: true });
      const response = await postRequest<Person, AddEmergencyContactsRequest>(
        API.PEOPLE,
        `/${personId}/emergency-contacts`,
        data,
      );

      set({
        person: response.data,
        loading: false,
        error: null,
      });
    } catch (error: any) {
      console.error('Error adding emergency contacts:', error);
      set({
        loading: false,
        error: error.message,
      });
      throw error;
    }
  },
  updateEmergencyContacts: async (personId: string, data: UpdateEmergencyContactRequest) => {
    try {
      set({ loading: true });
      await putRequest<void, UpdateEmergencyContactRequest>(
        API.PEOPLE,
        `/${personId}/emergency-contacts`,
        data,
      );

      set({ loading: false, error: null });
    } catch (error: any) {
      console.error('Error updating emergency contacts:', error);
      set({
        loading: false,
        error: error.message,
      });
      throw error;
    }
  },
  deleteEmergencyContacts: async (personId: string, contactIds: string[]) => {
    try {
      set({ loading: true });
      await deleteRequest<void>(API.PEOPLE, `/${personId}/emergency-contacts`, undefined, false, {
        ids: contactIds,
      });

      set({ loading: false, error: null });
    } catch (error: any) {
      console.error('Error deleting emergency contacts:', error);
      set({
        loading: false,
        error: error.message,
      });
      throw error;
    }
  },

  sendConnection: async (data) => {
    try {
      set({ loading: true });
      await sendConnection(data);
      set({ loading: false, error: null });
    } catch (error: any) {
      set({ loading: false, error: error.message });
      throw error;
    }
  },

  getMyConnections: async (params) => {
    try {
      set({ loading: true });
      const connections = await getMyConnections(params);
      set({ connections, loading: false, error: null });
      return connections;
    } catch (error: any) {
      set({ loading: false, error: error.message });
      return [];
    }
  },

  getPendingConnections: async (params) => {
    try {
      set({ loading: true });
      const pendingConnections = await getPendingConnections(params);
      set({ pendingConnections, loading: false, error: null });
    } catch (error: any) {
      set({ loading: false, error: error.message });
    }
  },

  acceptConnection: async (id) => {
    try {
      set({ loading: true });
      await acceptConnection(id);
      set({ loading: false, error: null });
    } catch (error: any) {
      set({ loading: false, error: error.message });
      throw error;
    }
  },

  declineConnection: async (id) => {
    try {
      set({ loading: true });
      await declineConnection(id);
      set({ loading: false, error: null });
    } catch (error: any) {
      set({ loading: false, error: error.message });
      throw error;
    }
  },

  updateReinvite: async (id, data) => {
    try {
      set({ loading: true });
      await updateReinvite(id, data);
      set({ loading: false, error: null });
    } catch (error: any) {
      set({ loading: false, error: error.message });
      throw error;
    }
  },

  deleteConnection: async (id) => {
    try {
      set({ loading: true });
      await deleteConnection(id);
      set({ loading: false, error: null });
    } catch (error: any) {
      set({ loading: false, error: error.message });
      throw error;
    }
  },
  getConnectionsByUserId: async (userId, params) => {
    try {
      return await getConnectionsByUserId(userId, params);
    } catch (error: any) {
      console.error('Error fetching connections by user ID:', error);
      throw error;
    }
  },
}));
